// src/App.js
import React, {startTransition, Suspense, useEffect, useState} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
import {Provider, useSelector} from 'react-redux';
import {PersistGate} from 'redux-persist/integration/react';
import {ToastContainer} from 'react-toastify';
// import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

import {persistor, store} from './redux/store';
import {HelmetProvider} from 'react-helmet-async';
import NavbarComponent from './components/NavBarComponents/NavbarComponent';
import NavbarLoggedInComponent from './components/NavBarComponents/NavbarLoggedInComponent';
import Footer from './components/LegalComponents/Footer';
import AuthCheck from './hooks/useAuthCheck';

import LandingPage from './components/LandingComponent/LandingPage';
import FallbackUI from "./components/FallbackUI/FallbackUI";
import ShowRoomComponent from "./components/ShowRoomComponent/ShowRoomComponent";
// import CustomerPortal from "./components/CustomerPortalComponent/CustomerPortal";
import UserUsageTracker from "./components/InternalToolsComponent/UserUsageTrackerComponent/UserUsageTracker";

// Lazy-load everything else
const OrderPage = React.lazy(() => import('./components/ConfigureComponent/ConfigurePage'));
const CartComponent = React.lazy(() => import('./components/CartComponent/CartComponent'));
const CustomerPortal = React.lazy(() => import('./components/CustomerPortalComponent/CustomerPortal'))
const ProfileComponent = React.lazy(() => import('./components/ProfileComponent/ProfileComponent'));
const EditProfileComponent = React.lazy(() => import('./components/ProfileComponent/EditProfileComponent'));
const CheckoutContainer = React.lazy(() => import('./components/CheckoutComponent/CheckoutContainer'));
const ContactComponent = React.lazy(() => import('./components/ContactComponent/ContactComponent'));
const SavedOrdersComponent = React.lazy(() => import('./components/SavedOrdersComponent/SavedOrdersComponent'));
const ResetPasswordPage = React.lazy(() => import('./modals/ResetPasswordPage'));
const OrderDetailsComponent = React.lazy(() => import('./components/CustomerPortalComponent/OrderDetails/OrderDetailsComponent'));
const CabinetQCComponent = React.lazy(() => import('./components/CustomerPortalComponent/CabinetQC/CabinetQCComponent'));
const OrderSummaryComponent = React.lazy(() => import('./components/CustomerPortalComponent/OrderSummary/OrderSummaryComponent'));
const OrderMatHardware = React.lazy(() => import('./components/OrderMatHardwareComponent/OrderMatHardwareComponent'));
const PrivacyComponent = React.lazy(() => import('./components/LegalComponents/PrivacyComponent'));
const TSComponent = React.lazy(() => import('./components/LegalComponents/TSComponent'));
const AboutUsComponent = React.lazy(() => import('./components/AboutUsComponent/AboutUsComponent'));
const ToolTipExplain = React.lazy(() => import('./components/ToolTipComponent/ToolTipExplainComponent'));
const GlobalSettings = React.lazy(() => import('./components/GlobalSettingsComponent/GlobalSettingsComponent'));
const DrawingTool = React.lazy(() => import('./components/DrawingComponent/DrawingTool'));
const TutorialComponent = React.lazy(() => import('./components/TutorialComponent/TutorialComponent'));
const OrderList = React.lazy(() => import('./components/InternalToolsComponent/OrderManagementComponent/OrderList'));
const OrderDetailPage = React.lazy(() => import('./components/InternalToolsComponent/OrderManagementComponent/OrderDetailPage'));
const InternalTools = React.lazy(() => import('./components/InternalToolsComponent/InternalTools'));
const VendorManagement = React.lazy(() => import('./components/InternalToolsComponent/VendorManagementComponent/VendorManagementComponent'));
const VendorComponent = React.lazy(() => import('./components/InternalToolsComponent/VendorComponent/VendorComponent'));
const VendorPOComponent = React.lazy(() => import('./components/InternalToolsComponent/VendorComponent/VendorPOComponent'));
const TrendingComponent = React.lazy(() => import('./components/InternalToolsComponent/TrendingComponent/TrendingComponent'));
const ArticleManagement = React.lazy(() => import('./components/InternalToolsComponent/ArticleManagementComponent/ArticleManagementComponent'));
const PDFViewer = React.lazy(() => import('./components/CustomerPortalComponent/PDFViewer'));
const CatalogInfo = React.lazy(() => import('./components/CatalogInfoComponent/CatalogInfo'));
const Finances = React.lazy(() => import('./components/InternalToolsComponent/FinancesComponent/Finances'));
const ChecklistComponent = React.lazy(() => import('./components/InternalToolsComponent/ChecklistComponent/ChecklistComponent'));
const GalleryComponent = React.lazy(() => import('./components/GalleryComponent/GalleryComponent'));
const SavedSettings = React.lazy(() => import('./components/SavedSettingsComponent/SavedSettingsComponent'));

// ChatBot remains lazy, deferred off the critical path
const LazyLoadedChatBot = React.lazy(() => import('./components/ChatComponent/LazyLoadedChatBot'));

function App() {
    const [showChatBot, setShowChatBot] = useState(false);

    // // Async GTM snippet
    // useEffect(() => {
    //     const script = document.createElement('script');
    //     script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-11552954224';
    //     script.async = true;
    //     document.head.appendChild(script);
    //     script.onload = () => {
    //         window.dataLayer = window.dataLayer || [];
    //
    //         function gtag() {
    //             window.dataLayer.push(arguments);
    //         }
    //
    //         gtag('js', new Date());
    //         gtag('config', 'AW-11552954224');
    //     };
    // }, []);

    // Defer ChatBot after paint
    useEffect(() => {
        startTransition(() => setShowChatBot(true));
    }, []);

    return (
        <HelmetProvider>
            <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                    <BrowserRouter>
                        <AuthCheck>
                            <Navbar/>

                            {showChatBot && (
                                <Suspense fallback={<FallbackUI message="Loading..."/>}>
                                    <LazyLoadedChatBot/>
                                </Suspense>
                            )}

                            {/* Only one Suspense around all the other lazies */}
                            <Suspense fallback={<FallbackUI message="Loading..."/>}>
                                <Routes>
                                    <Route path="/" element={<LandingPage/>}/>
                                    {/*<Route path="/showroom" element={<ShowRoomComponent/>}/>*/}
                                    <Route path="/cabinets/showroom" element={<ShowRoomComponent/>}/>
                                    {/*<Route path="/order" element={<OrderPage/>}/>*/}
                                    <Route path="/cabinets/:serialNumber" element={<OrderPage/>}/>
                                    <Route path="/cart" element={<CartComponent/>}/>

                                    <Route path="/user/profile" element={<ProfileComponent/>}/>
                                    <Route path="/user/edit-profile" element={<EditProfileComponent/>}/>
                                    <Route path="/checkout" element={<CheckoutContainer/>}/>
                                    <Route path="/customer-support/contact" element={<ContactComponent/>}/>
                                    <Route path="/user/customer-portal" element={<CustomerPortal/>}/>
                                    <Route path="/user/customer-portal/:orderId" element={<OrderDetailsComponent/>}/>
                                    <Route path="/api/cabinet/quality-control/:orderId/:positionName"
                                           element={<CabinetQCComponent/>}/>
                                    <Route path="/user/customer-portal/:orderId/summary"
                                           element={<OrderSummaryComponent/>}/>
                                    <Route path="/user/customer-portal/:orderId/materials&hardware"
                                           element={<OrderMatHardware/>}/>
                                    <Route path="/user/profile/saved-orders" element={<SavedOrdersComponent/>}/>
                                    <Route path="/reset-password" element={<ResetPasswordPage/>}/>
                                    <Route path="/the-sealab/legal/privacy" element={<PrivacyComponent/>}/>
                                    <Route path="/the-sealab/legal/terms" element={<TSComponent/>}/>
                                    <Route path="/the-sealab/about-us" element={<AboutUsComponent/>}/>
                                    <Route path="/learning/tool-tip/explain"
                                           element={<ToolTipExplain/>}/>
                                    <Route path="/cart/global-settings" element={<GlobalSettings/>}/>
                                    <Route path="/drawing" element={<DrawingTool/>}/>

                                    <Route path="/learning/tutorials" element={<TutorialComponent/>}/>
                                    <Route path="/internal/order-management"
                                           element={<OrderList/>}/>
                                    <Route path="/internal/order-management/:orderId"
                                           element={<OrderDetailPage/>}/>
                                    <Route path="/admin/internal-tools" element={<InternalTools/>}/>
                                    <Route path="/internal/vendors" element={<VendorManagement/>}/>
                                    <Route path="/internal/vendors/:vendorId" element={<VendorComponent/>}/>
                                    <Route path="/internal/vendors/:vendorId/purchase-order"
                                           element={<VendorPOComponent/>}/>
                                    <Route path="/internal/orders/trending"
                                           element={<TrendingComponent/>}/>
                                    <Route path="/internal/articles/management"
                                           element={<ArticleManagement/>}/>
                                    <Route path="/pdf-viewer" element={<PDFViewer/>}/>
                                    <Route path="/catalog/:serialNumber" element={<CatalogInfo/>}/>
                                    <Route path="/internal/finances" element={<Finances/>}/>
                                    <Route path="/internal/user-usage-tracker" element={<UserUsageTracker/>}/>
                                    <Route path="/admin/internal/:orderId/check-list"
                                           element={<ChecklistComponent/>}/>
                                    <Route path="/the-sealab/gallery" element={<GalleryComponent/>}/>
                                    <Route path="/cart/saved-settings" element={<SavedSettings/>}/>
                                    <Route path="*" element={<Navigate to="/cabinets/showroom" replace/>}/>
                                </Routes>
                            </Suspense>

                            <ToastContainer position="top-center" autoClose={2000} hideProgressBar/>
                        </AuthCheck>
                        <Footer/>
                    </BrowserRouter>
                </PersistGate>
            </Provider>
        </HelmetProvider>
    );
}

function Navbar() {
    const user = useSelector(state => state.user.user) || {};
    const isUserLoggedIn = Boolean(user.email && Object.keys(user).length);
    return isUserLoggedIn
        ? <NavbarLoggedInComponent/>
        : <NavbarComponent/>;
}

export default App;
